import { Alert, AlertProps } from "@mui/material";
import { MuiIconButton } from "../button/MuiButton";
import { MuiBox } from "../box/MuiBox";
import { MuiTypography } from "../typography/MuiTypography";
import { MuiAlertWrapper } from "./MuiAlert.style";

export const severityItems: Record<string, AlertProps["severity"]> = {
  warning: "warning",
  success: "success",
  info: "info",
  error: "error",
};

interface MuiAlertProps extends Omit<AlertProps, "children" | "icon"> {
  /** Message can be a string or any JSX/React node */
  message: React.ReactNode;
  /** Re-use MUI’s own severity type from AlertProps */
  severity?: AlertProps["severity"];
  /** Optional extra class name(s) for custom CSS overrides */
  className?: string;
  /** Custom icon element (e.g. <MyIcon />). If undefined, uses default Alert icon */
  icon?: React.ReactNode;
  /** Position of the custom icon: start (default) or end */
  iconPosition?: "start" | "end";
  /** Optional onClose handler for close icon */
  onIconClick?: () => void;
  /* to set the title of message e.g. notes*/
  alertTitle?: string;
  iconAreaLabel?: string;
}

export const MuiAlert: React.FC<MuiAlertProps> = ({
  message,
  severity = "warning",
  className,
  icon,
  iconPosition = "start",
  onIconClick,
  iconAreaLabel,
  alertTitle,
  ...rest
}) => {
  const renderIcon = icon ? (
    <MuiIconButton aria-label={iconAreaLabel} onClick={() => onIconClick?.()}>
      {icon}
    </MuiIconButton>
  ) : null;

  return (
    <MuiAlertWrapper>
      <Alert
        severity={severity}
        icon={false}
        className={className}
        variant="outlined"
        {...rest}
      >
        <MuiBox className="alert-box">
          {iconPosition === "start" && renderIcon}
          <MuiBox className="alert-message-container">
            {alertTitle && (
              <MuiTypography
                variant="h3Bold"
                component="h3"
                className="alert-title"
              >
                {alertTitle}
              </MuiTypography>
            )}
            <MuiTypography variant="h3" component="h3" className="message">
              {message}
            </MuiTypography>
          </MuiBox>
          {iconPosition === "end" && renderIcon}
        </MuiBox>
      </Alert>
    </MuiAlertWrapper>
  );
};
